<template>
  <div class="chart-wrapper-page">
    <div class="chart-card">
      <div class="chart-header">
        <h3 class="chart-title">Device Monthly Price</h3>
        <div class="year-filters">
          <button
            v-for="year in yearOptions"
            :key="year"
            :class="['year-btn', { active: selectedYear === year }]"
            @click="fetchData(year)"
          >
            {{ year }}
          </button>
        </div>
      </div>

      <div v-if="isLoading" class="loading-state">
        <icon name="spinner" spin class="spinner"></icon>
        <p>Loading chart data...</p>
      </div>

      <template v-else>
        <div class="stat-row">
          <div class="stat-card">
            <span class="stat-label">Average price</span>
            <span class="stat-value">{{ fmtEur(latest && latest.avg_price_cents) }}</span>
            <span class="stat-sub">{{ latestLabel }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Median price</span>
            <span class="stat-value">{{ fmtEur(latest && latest.median_price_cents) }}</span>
            <span class="stat-sub">
              Range {{ fmtEur(latest && latest.min_price_cents) }} – {{ fmtEur(latest && latest.max_price_cents) }}
            </span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Devices</span>
            <span class="stat-value">{{ fmtCount(latest, "devices_count") }}</span>
            <span class="stat-sub">With a valid price this month</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Avg. monthly change</span>
            <span class="stat-value" :class="changeClass(latest && latest.avg_monthly_change_cents)">
              {{ fmtChange(latest && latest.avg_monthly_change_cents) }}
            </span>
            <span class="stat-sub">
              {{ fmtCount(latest, "devices_with_change") }} devices ·
              {{ fmtCount(latest, "customers_with_change") }} customers changed
            </span>
          </div>
        </div>

        <div class="chart-container">
          <canvas ref="priceChart"></canvas>
        </div>

        <div class="table-wrapper">
          <table class="stats-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Devices</th>
                <th>Avg. price</th>
                <th>Min. price</th>
                <th>Max. price</th>
                <th>Median price</th>
                <th>Devices with change</th>
                <th>Customers with change</th>
                <th>Avg. monthly change</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in stats" :key="row.month" :class="{ empty: !hasData(row) }">
                <td>{{ monthLabel(row.month) }}</td>
                <td>{{ fmtCount(row, "devices_count") }}</td>
                <td>{{ fmtEur(row.avg_price_cents) }}</td>
                <td>{{ fmtEur(row.min_price_cents) }}</td>
                <td>{{ fmtEur(row.max_price_cents) }}</td>
                <td>{{ fmtEur(row.median_price_cents) }}</td>
                <td>{{ fmtCount(row, "devices_with_change") }}</td>
                <td>{{ fmtCount(row, "customers_with_change") }}</td>
                <td :class="changeClass(row.avg_monthly_change_cents)">
                  {{ fmtChange(row.avg_monthly_change_cents) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Chart from "chart.js";
import "vue-awesome/icons/spinner";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue")
  },
  data() {
    const thisYear = new Date().getFullYear();
    return {
      yearOptions: [thisYear - 2, thisYear - 1, thisYear],
      selectedYear: thisYear,
      stats: [],
      isLoading: false,
      chart: null
    };
  },
  computed: {
    ...mapGetters(["getUserSystem"]),
    // Latest month of the selected year that has data
    latest() {
      const withData = this.stats.filter(this.hasData);
      return withData.length ? withData[withData.length - 1] : null;
    },
    latestLabel() {
      return this.latest ? this.monthLabel(this.latest.month) : "No data";
    }
  },
  methods: {
    ...mapActions("axios_mod", ["simpleFetch"]),
    fetchData(year) {
      this.selectedYear = year;
      const data = {
        query: `
          query DeviceMonthPriceStats($year: Int!) {
            DeviceMonthPriceStats(year: $year) {
              month
              devices_count
              avg_price_cents
              min_price_cents
              max_price_cents
              median_price_cents
              devices_with_change
              customers_with_change
              avg_monthly_change_cents
            }
          }
        `,
        variables: { year }
      };
      this.isLoading = true;
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) this.stats = res.DeviceMonthPriceStats;
        })
        .finally(() => {
          this.isLoading = false;
          this.$nextTick(this.renderChart);
        });
    },
    renderChart() {
      if (!this.$refs.priceChart) return;
      if (this.chart) this.chart.destroy();
      const eur = key => this.stats.map(r => (r[key] == null ? null : r[key] / 100));
      const axisColor = "#718096";
      this.chart = new Chart(this.$refs.priceChart.getContext("2d"), {
        type: "line",
        data: {
          labels: this.stats.map(r => this.monthLabel(r.month).substring(0, 3)),
          datasets: [
            {
              label: "Average price",
              data: eur("avg_price_cents"),
              borderColor: "#5469d4",
              backgroundColor: "#5469d4",
              borderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              fill: false,
              lineTension: 0
            },
            {
              label: "Median price",
              data: eur("median_price_cents"),
              borderColor: "#dd6b20",
              backgroundColor: "#dd6b20",
              borderWidth: 2,
              borderDash: [6, 4],
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              fill: false,
              lineTension: 0
            },
            {
              label: "Min. price",
              data: eur("min_price_cents"),
              borderColor: "transparent",
              backgroundColor: "transparent",
              pointRadius: 0,
              pointHitRadius: 0,
              fill: false,
              lineTension: 0
            },
            {
              // Filled down to the min dataset to draw the min–max band
              label: "Max. price",
              data: eur("max_price_cents"),
              borderColor: "transparent",
              backgroundColor: "rgba(84, 105, 212, 0.12)",
              pointRadius: 0,
              pointHitRadius: 0,
              fill: "-1",
              lineTension: 0
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [{ gridLines: { display: false }, ticks: { fontColor: axisColor } }],
            yAxes: [{
              gridLines: { color: "#edf2f7", zeroLineColor: "#e2e8f0" },
              ticks: { beginAtZero: true, fontColor: axisColor, callback: v => "€" + v }
            }]
          },
          legend: {
            display: true,
            position: "bottom",
            labels: {
              boxWidth: 12,
              padding: 12,
              // Show the band once, as "Min – max range", instead of two entries
              filter: item => item.datasetIndex !== 2,
              generateLabels: chart =>
                Chart.defaults.global.legend.labels.generateLabels(chart).map(l =>
                  l.datasetIndex === 3
                    ? Object.assign(l, { text: "Min – max range", fillStyle: "rgba(84, 105, 212, 0.25)" })
                    : l
                )
            }
          },
          tooltips: {
            mode: "index",
            intersect: false,
            callbacks: {
              label: item => {
                const row = this.stats[item.index];
                const keys = ["avg_price_cents", "median_price_cents", "min_price_cents", "max_price_cents"];
                const labels = ["Average", "Median", "Min.", "Max."];
                return `${labels[item.datasetIndex]}: ${this.fmtEur(row[keys[item.datasetIndex]])}`;
              },
              afterBody: items => {
                const row = items.length ? this.stats[items[0].index] : null;
                if (!row || !this.hasData(row)) return [];
                return [
                  `Devices: ${row.devices_count}`,
                  `Avg. change: ${this.fmtChange(row.avg_monthly_change_cents)}`
                ];
              }
            }
          }
        }
      });
    },
    hasData(row) {
      return !!row && row.devices_count > 0;
    },
    monthLabel(month) {
      return `${MONTHS[parseInt(month.substring(5, 7), 10) - 1]} ${month.substring(0, 4)}`;
    },
    fmtEur(cents) {
      if (cents == null) return "—";
      return "€" + (cents / 100).toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    fmtChange(cents) {
      if (cents == null) return "—";
      const sign = cents > 0 ? "+" : cents < 0 ? "−" : "";
      return sign + this.fmtEur(Math.abs(cents));
    },
    // Months without data show "—" instead of 0
    fmtCount(row, key) {
      return this.hasData(row) ? row[key] : "—";
    },
    changeClass(cents) {
      if (cents == null || cents === 0) return "";
      return cents > 0 ? "positive" : "negative";
    }
  },
  watch: {
    getUserSystem() {
      this.fetchData(this.selectedYear);
    }
  },
  mounted() {
    this.fetchData(this.selectedYear);
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  }
};
</script>

<style scoped>
.chart-wrapper-page {
  padding: 24px;
}

.chart-card {
  background: white;
  padding: 0;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.year-filters {
  display: flex;
  gap: 8px;
  background: #f7fafc;
  padding: 4px;
  border-radius: 8px;
}

.year-btn {
  padding: 8px 20px;
  border: none;
  background: transparent;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;
}

.year-btn:hover {
  background-color: #edf2f7;
}

.year-btn.active {
  background-color: #5469d4;
  color: white;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.stat-label {
  font-size: 13px;
  font-weight: 500;
  color: #718096;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
}

.stat-sub {
  font-size: 12px;
  color: #a0aec0;
}

.chart-container {
  height: 400px;
  position: relative;
}

.table-wrapper {
  margin-top: 24px;
  overflow-x: auto;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.stats-table th,
.stats-table td {
  padding: 8px 12px;
  text-align: right;
  white-space: nowrap;
  border-bottom: 1px solid #edf2f7;
}

.stats-table th:first-child,
.stats-table td:first-child {
  text-align: left;
}

.stats-table th {
  font-weight: 600;
  color: #4a5568;
  background: #f7fafc;
}

.stats-table td {
  color: #2d3748;
}

.stats-table tr.empty td {
  color: #a0aec0;
}

.positive {
  color: #2f855a !important;
}

.negative {
  color: #c53030 !important;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #718096;
}

.loading-state .spinner {
  font-size: 32px;
  margin-bottom: 12px;
  color: #5469d4;
}

.loading-state p {
  margin: 0;
  font-size: 14px;
}

@media (max-width: 992px) {
  .stat-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .chart-wrapper-page {
    padding: 16px;
  }

  .chart-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .year-filters {
    width: 100%;
  }

  .year-btn {
    flex: 1;
    min-width: 0;
  }

  .chart-container {
    height: 300px;
  }
}
</style>
